/**
 * Contact Service Types
 *
 * Centralized type definitions for all contact-related data structures
 * used across server actions, API calls, and UI components.
 *
 * NOTE: The backend API uses different field names:
 * - Instead of 'role' and 'relationship', it uses 'isLegalPersonalRepresentative' and 'isBeneficiary' booleans
 * - The backend doesn't accept 'mobile' or 'isPrimaryContact' fields on create/update
 * - Our service layer handles the transformation between frontend and backend formats
 *
 */

import type { ApiErrorResponse } from '@/lib/api/direct-client';
import { z } from 'zod';
import { NON_PROVING_REASONS, type NonProvingReason } from '@/lib/constants';
import {
  addressStateSchema,
  refineAddressState
} from '@/lib/schemas/inventory-form-schemas';

/** Non-proving reason values (mirrors the backend NonProvingReason enum). */
const NON_PROVING_REASON_VALUES = NON_PROVING_REASONS.map(
  (r) => r.value
) as unknown as [NonProvingReason, ...NonProvingReason[]];

// Core Contact Types

/**
 * Identity-axis status on the contact. Mirrors the backend
 * `ContactIdentificationStatus` enum (the column itself stays VARCHAR, so the
 * `Contact.identificationStatus` field stays widened to `string`).
 */
export type ContactIdentificationStatus =
  | 'PENDING'
  | 'VERIFIED'
  | 'MANUALLY_CONFIRMED';

/**
 * Contact entity representing a person associated with a matter
 * Matches the backend API structure
 */
export interface Contact {
  id: number;
  matterId: number;
  /**
   * Matter-scoped `contact_matter` join row ID — the FK used by Communications
   * endpoints (`additionalRecipientContactMatterIds`, contact-thread
   * `participantId`). Distinct from `id`, which is the cross-matter contact.
   */
  contactMatterId?: number;
  // Individual contact fields (optional for organisation contacts)
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  /** A legal also-known-as, feeding court-form alias recitals. Not a nickname. */
  alias?: string | null;
  /**
   * How this person is actually addressed — "Bob" for a Robert. When set, it
   * supersedes the legal name in the email `recipient.firstName` /
   * `recipient.fullName` merge fields, so existing firm templates greet the
   * person by it without being edited. Individual contacts only.
   */
  preferredName?: string | null;
  // Organisation contact field (optional for individual contacts)
  organisationName?: string | null;
  // Common fields
  occupation?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  dateOfBirth?: string | null;
  /**
   * Date of death (YYYY-MM-DD). Optional supporting detail for a deceased
   * contact — the authoritative flag is `isDeceased`, not the presence of this
   * date. The matter's own decedent is on `matter.decedent*`, not here.
   */
  dateOfDeath?: string | null;
  /**
   * First-class "this contact has died" flag (e.g. a predeceased beneficiary).
   * This — not `dateOfDeath` presence — drives the "(deceased)" name suffix and
   * the entitlements-page warning banner. In the Add/Edit Contact form the
   * optional Date of Death field is only shown once this is ticked.
   */
  isDeceased?: boolean | null;
  notes?: string | null;
  matterNotes?: string | null;
  relationshipToDeceased?: string | null;
  /**
   * What this matter is asking this contact, which selects the discovery letter
   * they receive. `null` means they are not a letter recipient at all — which
   * is every contact until a lawyer makes one, so absence is the norm here
   * rather than a gap.
   */
  letterType?: string | null;
  isLegalPersonalRepresentative: boolean;
  /**
   * Only meaningful when isLegalPersonalRepresentative is true. Whether this
   * LPR is proving (applying for the grant) rather than non-proving ("leave
   * reserved" — remains a valid executor of record but is excluded from
   * generated documents). Defaults to true. See LF-5342.
   */
  isProvingLpr?: boolean | null;
  /**
   * Why this LPR is non-proving. Only populated when `isProvingLpr` is false;
   * null for proving LPRs. Drives the reason shown on the non-proving badge and
   * the editor reason picker. See LF-5372.
   */
  nonProvingReason?: NonProvingReason | null;
  isBeneficiary: boolean;
  isClient?: boolean; // For non-estate-administration matters
  /**
   * The PMS contact type chosen for this contact **on this matter**, in the firm's own catalogue.
   * Opaque to Vale — the shape is the provider's. Null/absent means nothing has been chosen, in
   * which case the contact is not filed on the PMS matter: there is deliberately no default.
   */
  pmsContactTypeId?: string | null;
  /**
   * The chosen type's display name, stored alongside the id so the card can render an existing
   * selection without calling the provider's catalogue — and therefore without the PMS being
   * reachable at all.
   */
  pmsContactTypeLabel?: string | null;
  isMinor?: boolean; // UI-only field: Minor status indicator for beneficiaries (DO NOT send to backend)
  isOfAge?: boolean; // Backend field: Indicates if beneficiary is of age (inverse of isMinor)
  // Per-matter bank-verification flag from the ContactMatter junction.
  // Distinct from `bankAccount.isVerified`, which is the contact-global
  // "standing belief" about the account itself and would leak across
  // matters. Drive the verification badge from this flag — never the
  // bankAccount-level one. See LF-3696. Optional in the type for the
  // benefit of mock/test factories; the BE always returns it on matter-
  // scoped reads.
  isBankVerified?: boolean;
  legalPersonalRepresentativeRole?:
    | 'executor'
    | 'applicant_for_letters_of_administration'
    | null;
  address?: {
    businessName?: string | null;
    streetAddress?: string | null;
    suburb?: string | null;
    state?: string | null;
    postcode?: string | null;
    poBox?: string | null;
    country?: string | null;
  } | null;
  bankAccount?: {
    bsb?: string | null;
    accountNumber?: string | null;
    accountName?: string | null;
    // Overseas / international details (present for non-Australian accounts).
    country?: string | null;
    swiftBic?: string | null;
    internationalAccountNumber?: string | null;
    bankName?: string | null;
    isVerified?: boolean;
    verificationNote?: string | null;
  } | null;
  // Backend returns these at root level
  primaryIdentificationFileId?: number;
  secondaryIdentificationFileId?: number;
  /**
   * Identity axis (independent of ConnectID identity verification and of the
   * matter-scoped bank verification — never conflate the three).
   * `PENDING` = not established · `VERIFIED` = established from an ID document
   * on file · `MANUALLY_CONFIRMED` = a law-firm user personally vouched for the
   * identity without an ID document (see `POST .../confirm-identity`).
   */
  identificationStatus?: ContactIdentificationStatus | string | null;
  /** When identity was established — for a manual confirmation, when the lawyer confirmed it. */
  identificationVerifiedAt?: string | null;
  /** How identity was confirmed. Populated only when `MANUALLY_CONFIRMED`. */
  identificationConfirmationNote?: string | null;
  /** LawFirmUser who manually confirmed the identity. Null unless `MANUALLY_CONFIRMED`. */
  identificationConfirmedById?: number | null;
  /** Display name of the confirming LawFirmUser. Null unless `MANUALLY_CONFIRMED`. */
  identificationConfirmedByName?: string | null;
  // Legacy nested structure (kept for compatibility)
  identificationDocuments?: {
    primaryIdFileId?: number;
    primaryIdFilename?: string;
    primaryIdUploadedAt?: string;
    secondaryIdFileId?: number;
    secondaryIdFilename?: string;
    secondaryIdUploadedAt?: string;
  };
  // Bank verification summaries from backend (array of active verifications, excludes SUPERSEDED/EXPIRED)
  verifications?: Array<{
    id: number;
    status:
      | 'REQUESTED'
      | 'IN_PROGRESS'
      | 'VERIFIED'
      | 'REVIEW'
      | 'FAILED'
      | 'EXPIRED'
      | 'MANUAL'
      | 'SUPERSEDED';
    confidenceLevel?: 'HIGHEST' | 'HIGH' | 'REVIEW_REQUIRED' | 'FAILED' | null;
    requestedAt: string;
    completedAt?: string | null;
    lawyerApprovedAt?: string | null;
    lawyerApprovedByName?: string | null;
  }>;
  // ---------------------------------------------------------------------------
  // Payment readiness (BENTAB-1, matter-scoped on the `contact_matter` join).
  // "Ready to Pay" is an explicit, reversible lawyer confirmation — NOT a
  // derived `bankVerified && declarationComplete` flag. `readyToPayConfirmedAt`
  // being set is the *sole* driver of Ready-to-Pay membership in the reworked
  // beneficiary tab (see `beneficiaries/derivation.ts`); the FE never
  // re-derives eligibility — it gates the Mark-Ready button on
  // `canMarkReadyToPay`, which the backend computes (§11.3). Optional here for
  // the benefit of mock/test factories; the BE always returns them on
  // matter-scoped reads.
  // ---------------------------------------------------------------------------
  /** When a law-firm user marked this beneficiary ready to pay; null = not ready (never-marked or unmarked). */
  readyToPayConfirmedAt?: string | null;
  /** Display name of the confirming law-firm user; null when system-set (rollout backfill) or not ready. */
  readyToPayConfirmedByName?: string | null;
  /** The lawyer's file note captured with the current Mark-Ready. */
  readyToPayNote?: string | null;
  /** Backend-computed eligibility for the Mark-Ready action; the FE never re-derives this. */
  canMarkReadyToPay?: boolean;
  /** Safe custody packets associated with this contact (optional, included by backend) */
  safeCustodyPackets?: ContactSafeCustodyPacket[];
  /** Matters associated with this contact (optional, included by backend) */
  matters?: ContactMatterAssociation[];
  createdAt: string;
  updatedAt: string;
  /**
   * What this contact is recorded as handling on this matter (EH-CR-002;
   * VALE-1760) — institution names and asset labels, empty for almost everyone.
   *
   * Non-empty means a delete WILL be refused, so the UI disables it and says
   * why rather than letting the reader click and meet a toast. Optional because
   * the narrower contact projections do not carry it.
   */
  handlingBlockers?: string[];
}

/**
 * Contact summary for list views
 */
export interface ContactSummary {
  id: number;
  matterId: number;
  fullName: string;
  email?: string | null;
  isLegalPersonalRepresentative: boolean;
  isProvingLpr?: boolean | null;
  nonProvingReason?: NonProvingReason | null;
  isBeneficiary: boolean;
}

// Request/Response Types

export interface CreateContactRequest {
  id?: number;
  // Individual contact fields (required if no organisationName)
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  alias?: string | null;
  // Organisation contact field (required if no firstName/lastName)
  organisationName?: string | null;
  // Common fields
  occupation?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  dateOfBirth?: string | null;
  dateOfDeath?: string | null;
  isDeceased?: boolean | null;
  notes?: string | null;
  matterNotes?: string | null;
  relationshipToDeceased?: string | null;
  isLegalPersonalRepresentative: boolean;
  isProvingLpr?: boolean | null;
  nonProvingReason?: NonProvingReason | null;
  isBeneficiary: boolean;
  isClient?: boolean | null; // For non-estate matters (ContactMatter.isClient)
  /** PMS contact type chosen for this contact on this matter. No default — absent means not filed. */
  pmsContactTypeId?: string | null;
  /** The chosen type's label, sent with the id so it can be rendered back with no catalogue call. */
  pmsContactTypeLabel?: string | null;
  isMinor?: boolean | null;
  isOfAge?: boolean | null;
  legalPersonalRepresentativeRole?:
    | 'executor'
    | 'applicant_for_letters_of_administration'
    | null;
  address?: {
    businessName?: string | null;
    streetAddress?: string | null;
    suburb?: string | null;
    state?: string | null;
    postcode?: string | null;
    poBox?: string | null;
    country?: string | null;
  } | null;
  bankAccount?: {
    bsb?: string | null;
    accountNumber?: string | null;
    accountName?: string | null;
    // Overseas / international details (present for non-Australian accounts).
    country?: string | null;
    swiftBic?: string | null;
    internationalAccountNumber?: string | null;
    bankName?: string | null;
    isVerified?: boolean;
    verificationNote?: string | null;
  } | null;
  identificationDocuments?: {
    primaryIdFileId?: number;
    primaryIdFilename?: string;
    primaryIdUploadedAt?: string;
    secondaryIdFileId?: number;
    secondaryIdFilename?: string;
    secondaryIdUploadedAt?: string;
  };
}

export interface UpdateContactRequest {
  // Individual contact fields
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  alias?: string | null;
  // Organisation contact field
  organisationName?: string | null;
  // Common fields
  occupation?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  dateOfBirth?: string | null;
  dateOfDeath?: string | null;
  isDeceased?: boolean | null;
  notes?: string | null;
  matterNotes?: string | null;
  relationshipToDeceased?: string | null;
  isLegalPersonalRepresentative?: boolean;
  isProvingLpr?: boolean | null;
  nonProvingReason?: NonProvingReason | null;
  isBeneficiary?: boolean;
  isClient?: boolean | null; // For non-estate matters (ContactMatter.isClient)
  /** PMS contact type chosen for this contact on this matter. No default — absent means not filed. */
  pmsContactTypeId?: string | null;
  /** The chosen type's label, sent with the id so it can be rendered back with no catalogue call. */
  pmsContactTypeLabel?: string | null;
  isMinor?: boolean | null;
  isOfAge?: boolean | null;
  legalPersonalRepresentativeRole?:
    | 'executor'
    | 'applicant_for_letters_of_administration'
    | null;
  address?: {
    businessName?: string | null;
    streetAddress?: string | null;
    suburb?: string | null;
    state?: string | null;
    postcode?: string | null;
    poBox?: string | null;
    country?: string | null;
  } | null;
  bankAccount?: {
    bsb?: string | null;
    accountNumber?: string | null;
    accountName?: string | null;
    // Overseas / international details (present for non-Australian accounts).
    country?: string | null;
    swiftBic?: string | null;
    internationalAccountNumber?: string | null;
    bankName?: string | null;
    isVerified?: boolean;
    verificationNote?: string | null;
  } | null;
  identificationDocuments?: {
    primaryIdFileId?: number;
    primaryIdFilename?: string;
    primaryIdUploadedAt?: string;
    secondaryIdFileId?: number;
    secondaryIdFilename?: string;
    secondaryIdUploadedAt?: string;
  };
}

export interface ContactListResponse {
  contacts: Contact[];
  total: number;
}

// Validation Schemas

/**
 * A contact's postal address. `state` is free text so an overseas contact's
 * province/region (e.g. a Canadian "British Columbia") can be captured, with
 * the Australian-state enum enforced only for Australian addresses — the same
 * country-conditional rule the backend's `PostalAddressDto` applies, so the
 * two layers accept and reject exactly the same values (LF-5933).
 *
 * Shared by the create and update schemas so the rule cannot drift between
 * adding a contact and editing one.
 */
const contactAddressSchema = z
  .object({
    businessName: z.string().optional().nullable(),
    streetAddress: z.string().optional().nullable(),
    suburb: z.string().optional().nullable(),
    state: addressStateSchema.nullable(),
    postcode: z.string().optional().nullable(),
    poBox: z.string().optional().nullable(),
    country: z.string().optional().nullable()
  })
  .superRefine(refineAddressState)
  .optional()
  .nullable();

export const CreateContactRequestSchema = z
  .object({
    id: z.number().optional(),
    // Individual contact fields (optional - required if no organisationName)
    firstName: z.string().max(50).optional().nullable(),
    middleName: z.string().optional().nullable(),
    lastName: z.string().max(50).optional().nullable(),
    alias: z.string().optional().nullable(),
    preferredName: z.string().max(100).optional().nullable(),
    // Organisation contact field (optional - required if no firstName/lastName)
    organisationName: z.string().max(200).optional().nullable(),
    // Common fields
    occupation: z.string().optional().nullable(),
    email: z
      .string()
      .optional()
      .nullable()
      .refine(
        (val) => !val || val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
        'Invalid email format'
      ),
    phoneNumber: z.string().optional().nullable(),
    dateOfBirth: z
      .string()
      .optional()
      .nullable()
      .transform((val) => (val === '' ? null : val)),
    dateOfDeath: z
      .string()
      .optional()
      .nullable()
      .transform((val) => (val === '' ? null : val)),
    isDeceased: z.boolean().optional().nullable(), // Backend field: first-class deceased flag
    notes: z.string().optional().nullable(),
    matterNotes: z.string().optional().nullable(),
    relationshipToDeceased: z.string().optional().nullable(),
    isLegalPersonalRepresentative: z.boolean().default(false),
    isProvingLpr: z.boolean().optional().nullable(), // Backend field: proving vs non-proving LPR ("leave reserved")
    nonProvingReason: z.enum(NON_PROVING_REASON_VALUES).optional().nullable(), // Backend field: reason an LPR is non-proving (LF-5372)
    isBeneficiary: z.boolean().default(false),
    isClient: z.boolean().optional().nullable(), // Backend field: client role on non-estate matters (ContactMatter.isClient)
    // Backend fields: the per-(contact, matter) PMS contact-type selection. Both must survive the
    // parse — Zod strips unknown keys, so omitting them here would silently drop the user's choice
    // between the card's submit and the save.
    pmsContactTypeId: z.string().optional().nullable(),
    pmsContactTypeLabel: z.string().optional().nullable(),
    isMinor: z.boolean().optional().nullable(), // UI-only, not sent to backend
    isOfAge: z.boolean().optional().nullable(), // Backend field
    legalPersonalRepresentativeRole: z
      .enum(['executor', 'applicant_for_letters_of_administration'])
      .optional()
      .nullable(),
    address: contactAddressSchema,
    bankAccount: z
      .object({
        bsb: z.string().optional().nullable(),
        accountNumber: z.string().optional().nullable(),
        accountName: z.string().optional().nullable(),
        // Overseas / international details (non-Australian accounts).
        country: z.string().optional().nullable(),
        swiftBic: z.string().optional().nullable(),
        internationalAccountNumber: z.string().optional().nullable(),
        bankName: z.string().optional().nullable(),
        isVerified: z
          .boolean()
          .nullish()
          .transform((val) => val ?? false),
        verificationNote: z.string().optional().nullable()
      })
      .optional()
      .nullable()
  })
  .refine(
    (data) => {
      // Must have either organisationName OR (firstName + lastName)
      const hasOrganisation = Boolean(data.organisationName?.trim());
      const hasIndividual = Boolean(
        data.firstName?.trim() && data.lastName?.trim()
      );
      return hasOrganisation || hasIndividual;
    },
    {
      message: 'Either organisation name or first and last name is required',
      path: ['firstName']
    }
  )
  .refine((data) => isDateOfDeathValid(data.dateOfDeath, data.dateOfBirth), {
    message:
      'Date of death must be in the past and not earlier than date of birth',
    path: ['dateOfDeath']
  });

/**
 * Cross-field check mirroring the backend `IsValidDateOfDeath` validator.
 * Treats null/undefined/empty as valid (the field is optional). Failing here
 * blocks submit; the backend will reject the same payload regardless.
 */
function isDateOfDeathValid(
  dateOfDeath: string | null | undefined,
  dateOfBirth: string | null | undefined
): boolean {
  if (!dateOfDeath) return true;
  const dod = new Date(dateOfDeath);
  if (Number.isNaN(dod.getTime())) return true; // format error already caught upstream
  const today = new Date();
  today.setUTCHours(23, 59, 59, 999);
  if (dod.getTime() > today.getTime()) return false;
  if (dateOfBirth) {
    const dob = new Date(dateOfBirth);
    if (!Number.isNaN(dob.getTime()) && dod.getTime() < dob.getTime())
      return false;
  }
  return true;
}

export const UpdateContactRequestSchema = z
  .object({
    // Individual contact fields
    firstName: z.string().max(50).optional().nullable(),
    middleName: z.string().optional().nullable(),
    lastName: z.string().max(50).optional().nullable(),
    alias: z.string().optional().nullable(),
    preferredName: z.string().max(100).optional().nullable(),
    // Organisation contact field
    organisationName: z.string().max(200).optional().nullable(),
    // Common fields
    occupation: z.string().optional().nullable(),
    email: z
      .string()
      .optional()
      .nullable()
      .refine(
        (val) => !val || val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
        'Invalid email format'
      ),
    phoneNumber: z.string().optional().nullable(),
    dateOfBirth: z
      .string()
      .optional()
      .nullable()
      .transform((val) => (val === '' ? null : val)),
    dateOfDeath: z
      .string()
      .optional()
      .nullable()
      .transform((val) => (val === '' ? null : val)),
    isDeceased: z.boolean().optional().nullable(), // Backend field: first-class deceased flag
    notes: z.string().optional().nullable(),
    matterNotes: z.string().optional().nullable(),
    relationshipToDeceased: z.string().optional().nullable(),
    isLegalPersonalRepresentative: z.boolean().optional(),
    isProvingLpr: z.boolean().optional().nullable(), // Backend field: proving vs non-proving LPR ("leave reserved")
    nonProvingReason: z.enum(NON_PROVING_REASON_VALUES).optional().nullable(), // Backend field: reason an LPR is non-proving (LF-5372)
    isBeneficiary: z.boolean().optional(),
    isClient: z.boolean().optional().nullable(), // Backend field: client role on non-estate matters (ContactMatter.isClient)
    // Backend fields: the per-(contact, matter) PMS contact-type selection. Both must survive the
    // parse — Zod strips unknown keys, so omitting them here would silently drop the user's choice
    // between the card's submit and the save.
    pmsContactTypeId: z.string().optional().nullable(),
    pmsContactTypeLabel: z.string().optional().nullable(),
    isMinor: z.boolean().optional().nullable(), // UI-only, not sent to backend
    isOfAge: z.boolean().optional().nullable(), // Backend field
    legalPersonalRepresentativeRole: z
      .enum(['executor', 'applicant_for_letters_of_administration'])
      .optional()
      .nullable(),
    address: contactAddressSchema,
    bankAccount: z
      .object({
        bsb: z.string().optional().nullable(),
        accountNumber: z.string().optional().nullable(),
        accountName: z.string().optional().nullable(),
        // Overseas / international details (non-Australian accounts).
        country: z.string().optional().nullable(),
        swiftBic: z.string().optional().nullable(),
        internationalAccountNumber: z.string().optional().nullable(),
        bankName: z.string().optional().nullable(),
        isVerified: z
          .boolean()
          .nullish()
          .transform((val) => val ?? false),
        verificationNote: z.string().optional().nullable()
      })
      .optional()
      .nullable()
  })
  .refine(
    (data) => {
      // For updates, we need to check if we have valid name data
      // Must have either organisationName OR (firstName + lastName)
      const hasOrganisation = Boolean(data.organisationName?.trim());
      const hasIndividual = Boolean(
        data.firstName?.trim() && data.lastName?.trim()
      );
      // Judge the pair only when the caller actually submits a pair.
      //
      // A whole-form save (`ContactFormCard`) always sends both name keys, so
      // its guard is unchanged: `{ firstName: 'Jane', lastName: '' }` still
      // fails here. A **single-field PATCH** — one name row committed on blur
      // by the attribute drawer (LF-6306) — sends only the key it edited, and
      // judging that against the pair refused it outright: `{ firstName:
      // 'Jane' }` could never be saved, because the rule read an *absent*
      // `lastName` as an emptied one.
      //
      // This is what the "Allow partial updates" comment above always claimed
      // and never did. The rule is not weakened where it bites — the backend
      // agrees, and deliberately: `UpdateMatterContactDto` is a
      // `PartialType`, which puts `@IsOptional()` on the anchor property
      // carrying `@HasRequiredContactName()`, so that cross-field rule is
      // skipped on updates by design. A blanked required name is refused
      // earlier and better, by the row's own `validate` (which names the
      // field and keeps the editor open) — see `contact-attribute-specs.ts`.
      const submitsBothNames =
        data.firstName !== undefined && data.lastName !== undefined;
      if (submitsBothNames) {
        return hasIndividual || hasOrganisation;
      }
      return true;
    },
    {
      message: 'Both first and last name are required for individual contacts',
      path: ['firstName']
    }
  )
  .refine((data) => isDateOfDeathValid(data.dateOfDeath, data.dateOfBirth), {
    message:
      'Date of death must be in the past and not earlier than date of birth',
    path: ['dateOfDeath']
  });

// Global Contacts List Types (for /contacts endpoint)

/**
 * Safe custody packet association for contact list view
 */
export interface ContactSafeCustodyPacket {
  id: number;
  label: string; // e.g., "SCP-2024-001"
}

/**
 * Matter association for contact list view
 */
/**
 * A matter a contact is linked to, as the contacts endpoints return it.
 *
 * Everything past `matterId` exists to feed `formatMatterTitle`
 * (`@/lib/utils/matter-display`) — the app's canonical matter-name helper —
 * through {@link formatContactMatterLabel}. Read the label from that helper
 * rather than assembling one of these fields directly, or the Contacts list
 * will name a matter differently from every other surface (VALE-1034).
 */
export interface ContactMatterAssociation {
  matterId: number;
  /** Matter type slug, e.g. `estate-administration` — `formatMatterTitle` branches on it. */
  type?: string | null;
  lawFirmInternalReference?: string | null; // e.g., "MAT-2024-001"
  /** The matter's `metadata.description` — what titles a business-law matter. */
  matterDescription?: string | null;
  decedentFirstName?: string | null;
  decedentMiddleName?: string | null;
  decedentLastName?: string | null;
  /** The matter's `metadata.clientNames` — the non-estate client-name fallback. */
  clientNames?: string[] | null;
  isLegalPersonalRepresentative?: boolean;
  isBeneficiary?: boolean;
  legalPersonalRepresentativeRole?: string | null;
}

/**
 * Extended contact for global list view with optional associations
 */
export interface GlobalContact extends Contact {
  /** Safe custody packets associated with this contact (when includeSafeCustodyPackets=true) */
  safeCustodyPackets?: ContactSafeCustodyPacket[];
  /** Matters associated with this contact (when includeMatters=true) */
  matters?: ContactMatterAssociation[];
  /** Display name computed from firstName/lastName or organisationName */
  displayName?: string;
}

/**
 * Filters for fetching global contacts list
 */
export interface GlobalContactsFilters {
  /** Include safe custody packet associations */
  includeSafeCustodyPackets?: boolean;
  /** Include matter associations */
  includeMatters?: boolean;
  /** Search term for filtering (query param: q) */
  q?: string;
  /** Page number (1-indexed) */
  page?: number;
  /** Items per page */
  limit?: number;
  /** Sort field */
  sortBy?: 'alphabetical' | 'createdAt';
  /** Sort order */
  sortOrder?: 'asc' | 'desc';
}

/**
 * Raw response from global contacts list endpoint (from backend)
 */
export interface GlobalContactsApiResponse {
  items: GlobalContact[];
  meta: {
    totalItems: number;
  };
}

/**
 * Processed response with computed pagination fields (used by components)
 */
export interface GlobalContactsResponse {
  items: GlobalContact[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Action Result Types

/**
 * Extended Error type with optional validation errors (Zod's flatten shape).
 *
 * Lives here rather than in `./hooks` since VALE-349: `toActionError` in
 * `./client` builds one too, and two declarations of one shape is how they
 * drift.
 */
export interface ErrorWithValidation extends Error {
  validationErrors?: Record<string, string[]>;
}

export interface ContactActionResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  validationErrors?: Record<string, string[]>;
  /**
   * **The API failure's status and body, carried rather than discarded**
   * (VALE-349).
   *
   * These actions flatten a `DirectApiError` into `{ success: false, error }`
   * for callers that only want a sentence — and every caller then rebuilt an
   * `Error` from that sentence, which is where the status went. That mattered
   * more than it looked: `saveFailureMessage`
   * (`components/attributes/use-attribute-commit.ts`) picks its copy **from the
   * status** — a dropped connection, a timeout, a class-validator 400 — so with
   * the status gone none of its branches could ever match on this surface, and
   * the reader got the raw wire string instead. Measured against the real API:
   * emptying an email to `a@b.c` rendered *"Email was not saved. email must be
   * an email"* — a database column name — where the enquiries drawer, which
   * never flattens, rendered the sentence written for her.
   *
   * Populated only for a `DirectApiError`; `toActionError` is what turns them
   * back into one, and is the only thing that should read them.
   */
  status?: number;
  response?: ApiErrorResponse;
}

// ============================================================================
// Bank Verification Types
// ============================================================================

/**
 * Response from requesting bank verification
 */
export interface RequestBankVerificationResponse {
  success: boolean;
  verificationId: number;
  magicLinkSent: boolean;
  sentTo: string;
  expiresAt: string;
}

/**
 * Lawyer-edited working copy of the beneficiary portal-access-link email, captured in the
 * preview/edit sheet and sent verbatim instead of re-resolving the template.
 */
export interface BeneficiaryEmailOverride {
  subject: string;
  body: string;
}

/**
 * A template-configured attachment the beneficiary receives automatically (configured on the firm's
 * email template). Display-only in the drawer — the FE never resends these; the backend attaches them.
 */
export interface PreviewTemplateAttachment {
  fileId: number;
  filename: string;
  mimeType: string | null;
}

/**
 * Rendered preview of the beneficiary portal-access-link email (firm fork → else Vale default).
 * `bodyHtml` has chips expanded and the portal "Continue" CTA substituted, but no outer wrapper or
 * firm signature — those are applied at send time.
 */
export interface PreviewBeneficiaryEmailResponse {
  subject: string;
  bodyHtml: string;
  recipientEmail: string;
  recipientName: string;
  /** Firm template attachments the beneficiary will receive automatically; shown read-only. */
  templateAttachments: PreviewTemplateAttachment[];
}

/**
 * A lawyer-selected attachment to send with a beneficiary's Send Request email, scoped to one
 * recipient. Wire shape sent to the backend — discriminated by `kind`:
 *  - `file`: an already-uploaded firm-owned File (raw upload), referenced by id.
 *  - `precedent`: a firm document precedent generated for this recipient at send time. `documentType`
 *    is required; an optional `templateId` (firm template) or `systemKey` (Vale system template)
 *    pins a specific template, else the firm's default for the type is used.
 */
export type BeneficiaryAttachmentSelection =
  | { kind: 'file'; fileId: number }
  | {
      kind: 'precedent';
      documentType: string;
      templateId?: number;
      systemKey?: string;
    };

/**
 * Response from resending verification link
 */
export interface ResendVerificationLinkResponse {
  success: boolean;
  magicLinkSent: boolean;
  sentTo: string;
  expiresAt: string;
}

/**
 * ConnectID verification details
 */
export interface ConnectIdVerificationDetails {
  verifiedName: string;
  verifiedAt: string;
  authServerId: string;
  bankName: string;
  providedBankAccount: boolean;
  /**
   * True when identity was verified via ConnectID's document + selfie
   * fallback (Daon) rather than a participating bank's login. `bankName`
   * carries no meaningful bank when this is true.
   */
  isDaonFallback: boolean;
}

/**
 * Zepto NPP validation details
 */
export interface ZeptoValidationDetails {
  result:
    | 'MATCH'
    | 'NO_MATCH'
    | 'CLOSE_MATCH'
    | 'ACCOUNT_CLOSED'
    | 'UNAVAILABLE'
    | 'NOT_CHECKED'
    | 'INVALID';
  validatedName?: string;
  checkedAt: string;
  attemptsCount: number;
}

/**
 * Name matching details
 */
export interface NameMatchingDetails {
  nameOnRecord: string;
  verifiedName: string;
  deltaScore: number;
  matched: boolean;
  /**
   * Set when the given + family names match but a middle-name component
   * differs materially (e.g. 'Middle name differs: "Peta" vs "Kita"'). When
   * present, the name check is flagged for review even if deltaScore >= 85.
   */
  componentMismatchReason?: string;
}

/**
 * Bank account details
 */
export interface BankAccountDetails {
  bsb: string;
  accountNumber: string;
  accountName: string;
}

/**
 * Lawyer review details
 */
export interface LawyerReviewDetails {
  reviewedAt: string;
  reviewedBy: string;
  approved: boolean;
  notes?: string;
}

/**
 * Full bank verification details response
 */
/**
 * The identity attributes ConnectID can return, in the order the ledger renders
 * them. This array is the set the comparison is defined over — the backend sends
 * one entry per member, so a surface enumerates this rather than listing
 * attributes of its own.
 */
export const IDENTITY_ATTRIBUTE_KEYS = [
  'name',
  'middleName',
  'dateOfBirth',
  'address',
  'email',
  'phone'
] as const;

export type IdentityAttributeKey = (typeof IDENTITY_ATTRIBUTE_KEYS)[number];

/**
 * `not-returned` must never render as agreement — an attribute nobody could
 * compare is the commonest outcome on a thin file and the easiest to misread.
 */
export type IdentityAttributeOutcome =
  | 'matches'
  | 'differs'
  | 'added-to-file'
  | 'not-returned';

export interface IdentityAttributeComparison {
  attribute: IdentityAttributeKey;
  verifiedValue: string | null;
  recordedValue: string | null;
  outcome: IdentityAttributeOutcome;
}

export interface BankVerificationDetails {
  verificationId: number;
  status:
    | 'REQUESTED'
    | 'IN_PROGRESS'
    | 'VERIFIED'
    | 'REVIEW'
    | 'FAILED'
    | 'EXPIRED'
    | 'MANUAL'
    | 'SUPERSEDED';
  confidenceLevel: 'HIGHEST' | 'HIGH' | 'REVIEW_REQUIRED' | 'FAILED';
  requestedAt: string;
  completedAt?: string;
  connectIdVerification?: ConnectIdVerificationDetails;
  zeptoValidation?: ZeptoValidationDetails;
  nameMatching?: NameMatchingDetails;
  /**
   * Lock 3, widened. Absent on Path C, where no ConnectID identity was captured
   * and there is nothing to compare.
   */
  identityComparison?: IdentityAttributeComparison[];
  bankAccount?: BankAccountDetails;
  lawyerReview?: LawyerReviewDetails;
  accountNameOverridden: boolean;
}

/**
 * Request body for approving bank verification
 */
export interface ApproveBankVerificationRequest {
  notes?: string;
  /**
   * True when the solicitor chose to keep the file's date of birth over the
   * bank-verified one. Only meaningful on a verification whose comparison
   * reports a date-of-birth divergence.
   */
  keepRecordedDateOfBirth?: boolean;
}

/**
 * Body for `POST /matters/:matterId/contacts/:contactId/confirm-identity` —
 * a law-firm user vouching for a contact's identity with no ID document on
 * file. The note is the record of *how* identity was established and is
 * **mandatory**: the backend DTO rejects an empty / whitespace-only value.
 */
export interface ConfirmContactIdentityRequest {
  note: string;
}

/**
 * Single item from the contact verifications list endpoint
 * GET /matters/:matterId/contact-verifications
 */
export interface ContactVerificationListItem {
  verificationId: number;
  contactId: number;
  status:
    | 'REQUESTED'
    | 'IN_PROGRESS'
    | 'VERIFIED'
    | 'REVIEW'
    | 'FAILED'
    | 'EXPIRED'
    | 'MANUAL'
    | 'SUPERSEDED';
  confidenceLevel: 'HIGHEST' | 'HIGH' | 'REVIEW_REQUIRED' | 'FAILED' | null;
  requestedAt: string;
  completedAt: string | null;
  lawyerApprovedAt: string | null;
}

/**
 * Summary counts across all verifications for a matter
 * GET /matters/:matterId/contact-verifications/summary
 */
export interface ContactVerificationSummary {
  total: number;
  requested: number;
  inProgress: number;
  verified: number;
  review: number;
  failed: number;
  expired: number;
  superseded: number;
  approved: number;
}

/**
 * Response from approving bank verification
 */
export interface ApproveBankVerificationResponse {
  success: boolean;
  message: string;
  bankAccount: {
    bsb: string;
    accountNumber: string;
    accountName: string;
    isVerified: boolean;
    verificationNote: string;
  };
}

/**
 * One category of record attached to a contact, as contact merge presents it —
 * "Entitlements as beneficiary", never a table name. The backend owns both the
 * category list and its wording (`CONTACT_ATTACHMENT_LABELS`), so the UI renders
 * whatever it is given rather than keeping a second list that can drift.
 */
export interface ContactMergeAttachment {
  key: string;
  label: string;
  /** How many of these move to the surviving contact. */
  moving: number;
  /** How many are destroyed because the survivor already occupies that slot. */
  dropped: number;
}

/** A field where both records hold a value and the values differ. */
export interface ContactMergeFieldConflict {
  field: string;
  /** What to call this field. Supplied by the API — the UI keeps no field list. */
  label: string;
  survivorValue: unknown;
  loserValue: unknown;
}

export interface ContactMergePreview {
  survivor: GlobalContact;
  mergeContact: GlobalContact;
  attachments: ContactMergeAttachment[];
  totalMoving: number;
  totalDropped: number;
  conflicts: ContactMergeFieldConflict[];
  /** Suggested by attached history rather than by which record is newer. */
  recommendedSurvivorId: number;
}

export interface ContactMergeResult {
  survivorId: number;
  mergedContactId: number;
  totalMoved: number;
  totalDropped: number;
  mergeRecordId: number;
}

/** A signal that two contacts are the same person. `name` is always present. */
export type ContactMatchSignal = 'name' | 'email' | 'phone' | 'dateOfBirth';

/** One matter a candidate contact is on — the number a lawyer quotes. */
export interface ContactDuplicateMatter {
  matterId: number;
  /** The firm's own matter reference; null when the firm has not set one. */
  reference: string | null;
}

export interface ContactDuplicateContact {
  id: number;
  firstName: string | null;
  lastName: string | null;
  organisationName: string | null;
  email: string | null;
  phoneNumber: string | null;
  identificationStatus: string | null;
  createdAt: string;
  /** Records attached to this contact — what a merge would move. */
  attachmentCount: number;
  /** The matters this contact is on, oldest first. */
  matters: ContactDuplicateMatter[];
}

/**
 * One suggested duplicate pair. A suggestion, never an instruction — the API
 * requires an identical name to offer one, and a lawyer confirms or dismisses it.
 */
export interface ContactDuplicateCandidate {
  contactA: ContactDuplicateContact;
  contactB: ContactDuplicateContact;
  matchedOn: ContactMatchSignal[];
  /** The values that actually matched, so the suggestion can be checked. */
  matchedValues: {
    name: string;
    email?: string;
    phone?: string;
    dateOfBirth?: string;
  };
  confidence: 'high' | 'medium';
}
